import { ROUTES } from 'src/router/const'

/**
 * Config for ConfirmToDeleteCard. `confirmPhrase` is omitted when the phrase
 * is passed as a prop. `icon`, `color`, and `textClass` fall back to trash,
 * negative, and text-negative on the card.
 * @typedef {Object} ConfirmToDeleteConfig
 * @property {string} [icon]
 * @property {string} [color]
 * @property {string} [textClass]
 * @property {string} title
 * @property {string} description
 * @property {string} consequence
 * @property {string} buttonLabel
 * @property {string} confirmTitle
 * @property {string} confirmBody
 * @property {string} confirmLabel
 * @property {string} confirmNote
 * @property {string} [confirmPhrase]
 * @property {string} inputId
 */

// Input maxlength constants for security validation
export const MAX_LENGTH = {
  DOCUMENT: 10000, // For document editors (TinyMCE, assessment documents)
  LONG_TEXT: 5000, // For textareas, notes, long text inputs
  MEDIUM_TEXT: 255, // For names, titles, descriptions, general inputs
  SHORT_TEXT: 40, // For very short inputs (person names, codes, etc.)
}

/** Quasar field chrome by type. Bind with `v-bind="FIELD_CHROME.form"` (etc). */
export const FIELD_CHROME = {
  form: {
    outlined: true,
    color: 'brand',
    bgColor: 'surface-muted',
    inputClass: 'text-primary',
  },
  flush: {
    borderless: true,
    dense: true,
    color: 'brand',
    bgColor: 'surface-dark',
  },
  embedded: {
    borderless: true,
    dense: true,
  },
}

/** Height classes — layout, not a 4th type. Pair with FIELD_CHROME.
 *  sm = 40px, md = 2.5rem, lg = 3rem (see `_quasar-override.scss`). */
export const FIELD_SIZE = {
  sm: 'field-size-sm',
  md: 'field-size-md',
  lg: 'field-size-lg',
}

// TODO(cookie-armor): expand NAV_LINKS once Cookie Armor product surfaces are defined.
// When: once Cookie Armor feature work begins.
export const NAV_LINKS = [
  {
    name: ROUTES.OVERVIEW,
    path: `/${ROUTES.APP}`,
    label: 'Overview',
    icon: 'layout-panel-top',
  },
  {
    name: ROUTES.BANNERS,
    path: `/${ROUTES.BANNERS}`,
    label: 'Banners',
    icon: 'cookie',
    exact: false,
  },
  {
    name: ROUTES.ANALYTICS,
    path: `/${ROUTES.ANALYTICS}`,
    label: 'Analytics',
    icon: 'chart-column',
  },
  {
    name: ROUTES.SETTINGS,
    path: `/${ROUTES.SETTINGS}`,
    label: 'Settings',
    icon: 'settings',
    exact: false,
  },
  {
    name: ROUTES.FAQ,
    path: `/${ROUTES.FAQ}`,
    label: 'Help Center',
    icon: 'help-circle',
    exact: false,
  },
]

/**
 * Error UI: pass one object into BasicError as `error-config`.
 * Only treat HTTP 404 as not-found — do not synthesize 404 in pages.
 *
 * Bases (`ERROR_CONFIG_DEFAULT`, `ERROR_CONFIG_BANNERS`) hold 404/nav fields only —
 * use them for spreading into composites, not as call-site configs.
 * Composites = base + `subject` for non-404 load copy. Add a new composite when a
 * call site needs distinct load wording.
 *
 * Optional: `retryIcon` — Lucide name for the "Try again" button (default: `refresh-cw`).
 * Optional: `notFoundSubtitle` — 404 subtitle copy (default: not-found availability message).
 * Optional: `showActions` — show action buttons (default: true).
 */
export const ERROR_CONFIG_DEFAULT = {
  notFoundTitle: 'Data',
  listRoute: ROUTES.OVERVIEW,
  listLabel: 'Go to Overview',
}

export const ERROR_CONFIG_BANNERS = {
  notFoundTitle: 'Banner',
  listRoute: ROUTES.BANNERS,
  listLabel: 'Go to Banners',
}

export const ERROR_CONFIG_FAQ = {
  notFoundTitle: 'Help Topic',
  listRoute: ROUTES.FAQ,
  listLabel: 'Go to Help Center',
}

export const ERROR_CONFIG_OVERVIEW = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'overview',
}

export const ERROR_CONFIG_BANNERS_INDEX = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'banners',
}

export const ERROR_CONFIG_ANALYTICS = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'analytics',
}

export const ERROR_CONFIG_TEAM_MEMBERS = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'team members',
}

export const ERROR_CONFIG_EMAIL_PREFERENCES = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'email preferences',
}

export const ERROR_CONFIG_DASHBOARD_STATS = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'dashboard stats',
}

export const ERROR_CONFIG_ACTIVE_BANNERS = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'active banners',
}

export const ERROR_CONFIG_EVENT_TREND = {
  ...ERROR_CONFIG_DEFAULT,
  subject: 'event trend',
}

export const ERROR_CONFIG_BANNER_DETAIL = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'banner',
}

export const ERROR_CONFIG_FAQ_TOPIC = {
  ...ERROR_CONFIG_FAQ,
  subject: 'help topic',
}

export const ERROR_CONFIG_FAQ_ARTICLE = {
  ...ERROR_CONFIG_FAQ,
  notFoundTitle: 'Help Article',
  subject: 'help article',
}

export const ERROR_CONFIG_BANNER_CREATED = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'banner details',
}

export const ERROR_CONFIG_SCAN_RESULTS = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'scan results',
}

export const ERROR_CONFIG_SCAN_HISTORY = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'scan history',
}

export const ERROR_CONFIG_SCAN_PROGRESS = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'scan progress',
}

export const ERROR_CONFIG_SCAN_DETAILS = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'scan details',
}

export const ERROR_CONFIG_EVENT_STATS = {
  ...ERROR_CONFIG_BANNERS,
  subject: 'events stats',
}

export const ERROR_CONFIG_CONSENT_DETAIL = {
  ...ERROR_CONFIG_BANNERS,
  notFoundTitle: 'Consent',
  subject: 'consent record',
  notFoundSubtitle: 'The consent ID may be wrong or belong to a different banner.',
  showActions: false,
}

/** BasicError layout variants — omit `variant` for full-page (default). */
export const BASIC_ERROR_VARIANTS = {
  INLINE: 'inline',
  INLINE_ROW: 'inline-row',
  SECTION: 'section',
}

/** Synthetic 404 error object for client-side not-found states (e.g. invalid static route params). */
export const NOT_FOUND_ERROR = Object.freeze({ response: { status: 404 } })

/** Synthetic non-404 error for a client-side load failure that has no HTTP response. */
export const LOAD_ERROR = Object.freeze({ response: {} })

export const AUTH0_ERROR_CODE = {
  ACCESS_DENIED: 'access_denied',
}

export const AUTH0_ERROR_DESCRIPTION = {
  EMAIL_NOT_VERIFIED: 'email_not_verified',
  ACCOUNT_LOCKED: 'account_locked',
}

/** Confirmed `/me` and API 401 string `error` values (not nested `error.error`). */
export const AUTH_SESSION_ERROR_CODE = {
  ACCOUNT_LOCKED: AUTH0_ERROR_DESCRIPTION.ACCOUNT_LOCKED,
  MEMBER_REMOVED: 'member_removed',
  ACCOUNT_DELETED: 'account_deleted',
}

export const AUTH_SESSION_EXPIRED_ORG_ENDED_SUBLINE = 'Contact your organization administrator.'

export const AUTH_ERROR_REASON = {
  AUTH0: 'auth0',
  SESSION_EXPIRED: 'session_expired',
  NOT_PROVISIONED: 'not_provisioned',
  BOOTSTRAP_FAILED: 'bootstrap_failed',
  EMAIL_NOT_VERIFIED: AUTH0_ERROR_DESCRIPTION.EMAIL_NOT_VERIFIED,
  ACCOUNT_LOCKED: AUTH0_ERROR_DESCRIPTION.ACCOUNT_LOCKED,
  TRIAL_EXPIRED: 'trial_expired',
}

/** Brand CTA on AuthErrorPanel. Locked has none; every other reason sets one. */
export const AUTH_MAIN_ACTION = {
  RETRY_BOOTSTRAP: 'retryBootstrap',
  LOGIN: 'login',
  CONTACT_SUPPORT: 'contactSupport',
}

// `headlineAccent` is optional and renders inline after `headline` in the brand
// color. `loading` / `logout` are read by AuthLoadingPanel, which draws the
// spinner and needs no `icon`; every reason below is read by AuthErrorPanel.
// `mainActionLabel` + `mainAction` are the brand CTA (omitted for locked).
export const AUTH_SCREEN_COPY = {
  loading: {
    headline: 'Signing you in…',
    subline: ['Loading your account.'],
  },
  logout: {
    headline: 'Signing you out…',
    subline: ['Ending your session securely.'],
  },
  [AUTH_ERROR_REASON.AUTH0]: {
    headline: "Couldn't sign you in",
    subline: ['Try again, or sign in with a different email.'],
    icon: 'lock',
    secondaryActionLabel: 'Sign in with a different email',
    mainActionLabel: 'Try again',
    mainActionIcon: 'refresh-cw',
    mainAction: AUTH_MAIN_ACTION.RETRY_BOOTSTRAP,
  },
  [AUTH_ERROR_REASON.NOT_PROVISIONED]: {
    headline: 'No account found',
    subline: [
      "This email isn't registered.",
      'Contact your administrator, or sign in with a different email.',
    ],
    icon: 'user-x',
    secondaryActionLabel: 'Sign in with a different email',
    mainActionLabel: 'Try again',
    mainActionIcon: 'refresh-cw',
    mainAction: AUTH_MAIN_ACTION.RETRY_BOOTSTRAP,
  },
  [AUTH_ERROR_REASON.SESSION_EXPIRED]: {
    headline: 'Session expired',
    subline: ['For your security, sign in again to continue.'],
    icon: 'clock',
    secondaryActionLabel: 'Sign in with a different email',
    mainActionLabel: 'Try again',
    mainActionIcon: 'refresh-cw',
    mainAction: AUTH_MAIN_ACTION.RETRY_BOOTSTRAP,
  },
  [AUTH_ERROR_REASON.BOOTSTRAP_FAILED]: {
    headline: "Couldn't load your account",
    subline: ['Wait a moment and try again.'],
    icon: 'wifi-off',
    secondaryActionLabel: 'Sign in with a different email',
    mainActionLabel: 'Try again',
    mainActionIcon: 'refresh-cw',
    mainAction: AUTH_MAIN_ACTION.RETRY_BOOTSTRAP,
  },
  // Shares this bank with the genuine failures above, but it is NOT one: it is the
  // expected next step immediately after a successful signup, which is why
  // boot/auth0.js skips capturing it as an exception. The copy congratulates
  // rather than warns, and frames verification as the last small step.
  [AUTH_ERROR_REASON.EMAIL_NOT_VERIFIED]: {
    headline: 'Welcome to',
    headlineAccent: 'Cookie Armor',
    subline: [
      'We sent you a verification link.',
      'Open your email to confirm your address.',
      "When you're done, sign in to continue.",
    ],
    icon: 'party-popper',
    secondaryActionLabel: 'Sign in with a different email',
    mainActionLabel: "I'm verified, sign in",
    mainActionIcon: 'arrow-right',
    mainAction: AUTH_MAIN_ACTION.LOGIN,
  },
  [AUTH_ERROR_REASON.ACCOUNT_LOCKED]: {
    headline: 'Account Paused',
    subline: ['This account has been paused. Contact support for assistance.'],
    icon: 'lock',
    secondaryActionLabel: 'Sign in with a different email',
  },
  [AUTH_ERROR_REASON.TRIAL_EXPIRED]: {
    headline: 'Your trial has ended.',
    subline: [
      "Don't worry - your banners, scans, and settings are safely stored.",
      "Contact us to align payment, and you'll pick up right where you left off.",
    ],
    icon: 'clock',
    mainActionLabel: 'Contact us to continue',
    mainAction: AUTH_MAIN_ACTION.CONTACT_SUPPORT,
    secondaryActionLabel: 'Sign in with a different email',
  },
}

/**
 * Gate panel ids — not route names.
 * The two session panels are pre-session; some are onboarding steps; some are
 * public-route session panels for already-authenticated visitors.
 */
export const GATE_PANELS = {
  SESSION_LOADING: 'session_loading',
  SESSION_ERROR: 'session_error',
  FOUNDER_WELCOME: 'founder_welcome',
  INVITEE_WELCOME: 'invitee_welcome',
  TERMS: 'terms',
  INVITE_SESSION: 'invite_session',
  EMAIL_VERIFIED_SESSION: 'email_verified_session',
}

/**
 * Why an invite verification came back not-valid.
 *
 * INVALID / EXPIRED are the backend's own verdicts on the token, and share one
 * copy block (it names both). UNAVAILABLE is client-side only: the request never
 * produced a verdict, so the token may be perfectly good and the landing must
 * not tell the invitee to ask for a new one.
 */
export const INVITE_VERIFICATION_REASON = {
  INVALID: 'invalid',
  EXPIRED: 'expired',
  UNAVAILABLE: 'unavailable',
}

export const ONBOARDING_FLOW_TYPE = {
  FOUNDER: 'founder',
  INVITEE: 'invitee',
}

/**
 * Onboarding copy — keeps the founder (name-your-account) and invitee (you-joined)
 * flows visibly distinct. `{account}` is interpolated with the account name.
 */
export const ONBOARDING_COPY = {
  acceptInvite: {
    loadingLabel: 'Checking your invitation…',
    noToken: {
      icon: 'user-check',
      headline: 'Sign in to Cookie Armor',
      subline: ['Sign in to continue to your account.'],
      ctaLabel: 'Sign in',
      ctaIcon: 'arrow-right',
    },
    valid: {
      headline: "You've been invited to",
      headlineAccent: 'Cookie Armor',
      icon: 'mail-check',
      ctaLabel: 'Accept invitation & sign in',
      ctaIcon: 'arrow-right',
      accountFallback: 'your account',
    },
    // Opaque dead token: may be spent, expired, or junk — lead with sign-in
    // (they may already have access) and keep admin as a fallback only.
    invalid: {
      icon: 'alert-circle',
      headline: 'Invitation not valid',
      subline: [
        'This invitation link is no longer valid or expired.',
        'If you already have access, sign in. If not, ask your admin for a new invite.',
      ],
      ctaLabel: 'Sign in',
      ctaIcon: 'arrow-right',
    },
    // Deliberately says nothing about the link: we never got a verdict on it.
    unavailable: {
      icon: 'wifi-off',
      headline: "We couldn't check your invitation",
      subline: ['Something went wrong on our side, not with your link.', 'Try again in a moment.'],
      ctaLabel: 'Try again',
      ctaIcon: 'refresh-cw',
      secondaryCtaLabel: 'Sign in',
    },
  },
  founderWelcome: {
    headline: "Let's set up your account",
    subline: [
      "Fill in the details and you're ready to go.",
      'You can update these anytime in settings.',
    ],
    icon: 'building',
    ctaLabel: 'Next',
    ctaIcon: 'arrow-right',
    // "Account name" rather than "Company name": right after signup, "account"
    // reads as the visitor's own login, not the tenant they're naming.
    accountNameLabel: 'Account name',
    placeholder: 'e.g. Acme Inc.',
    companyNameLabel: 'Company name (optional)',
    companyNamePlaceholder: 'e.g. Acme Inc.',
    countryLabel: 'Country (optional)',
    countryPlaceholder: 'Select a country',
  },
  inviteeWelcome: {
    headline: "You've joined {account}",
    subline: ["Add your name and you're in."],
    icon: 'party-popper',
    ctaLabel: 'Next',
    ctaIcon: 'arrow-right',
  },
  termsWelcome: {
    headline: 'Terms of Usage',
    subline: ['Please read and accept to continue.'],
    icon: 'file-text',
    ctaLabel: 'Finish setup',
    ctaIcon: 'arrow-right',
    disclaimer: 'By clicking Finish setup, you agree to the Terms of Usage.',
  },
  // Shared by both onboarding panels — the only way out of the gate without
  // finishing. Names the email rather than the account because the account
  // already exists server-side: this abandons setup and signs the visitor out,
  // and the next sign-in with the same email lands back on the same panel.
  // Kept identical to AUTH_SCREEN_COPY's secondaryActionLabel — same button,
  // same action, and one visitor can meet both banks in a single session.
  secondaryActionLabel: 'Sign in with a different email',
  nameFields: {
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
  },
}

/** Auth0 email-verification result states, resolved from the redirect query params. */
export const EMAIL_VERIFICATION_STATES = {
  VERIFIED: 'verified',
  ALREADY_VERIFIED: 'already_verified',
  INVALID: 'invalid',
  NO_RESULT: 'no_result',
}

/**
 * Copy for the email-verification landing, keyed by EMAIL_VERIFICATION_STATES.
 * Auth0's own `message` param is never rendered — it is untrusted URL input.
 */
export const EMAIL_VERIFICATION_COPY = {
  [EMAIL_VERIFICATION_STATES.VERIFIED]: {
    icon: 'mail-check',
    headline: 'Your email is verified',
    subline: ['Sign in to continue to your account.'],
    ctaLabel: 'Sign in',
    ctaIcon: 'arrow-right',
  },
  [EMAIL_VERIFICATION_STATES.ALREADY_VERIFIED]: {
    icon: 'mail-check',
    headline: 'Your email is already verified',
    subline: ['This link was already used.', 'Sign in to continue.'],
    ctaLabel: 'Sign in',
    ctaIcon: 'arrow-right',
  },
  [EMAIL_VERIFICATION_STATES.INVALID]: {
    icon: 'alert-circle',
    headline: 'This link is no longer valid',
    subline: ["Sign in and we'll email you a fresh verification link."],
    ctaLabel: 'Sign in',
    ctaIcon: 'arrow-right',
  },
  [EMAIL_VERIFICATION_STATES.NO_RESULT]: {
    icon: 'user-check',
    headline: 'Sign in to Cookie Armor',
    subline: ['Sign in to continue to your account.'],
    ctaLabel: 'Sign in',
    ctaIcon: 'arrow-right',
  },
}

/**
 * Copy for public-route session panels (already-authenticated visitors on
 * /accept-invite or /email-verified). `{currentEmail}` / `{invitedEmail}`
 * are interpolated by the consumer.
 */
export const SESSION_PANEL_COPY = {
  inviteSession: {
    loadingLabel: 'Checking your invitation…',
    mismatch: {
      icon: 'alert-circle',
      headline: 'This invitation is for a different address',
      subline: [
        "You're signed in as {currentEmail}.",
        'This invitation was sent to {invitedEmail}.',
      ],
      ctaLabel: 'Switch account & accept',
      ctaIcon: 'users',
      secondaryCtaLabel: 'Continue to your account',
    },
    match: {
      icon: 'mail-check',
      headline: "You're already signed in",
      subline: ['This invitation was sent to {invitedEmail}, the account you are signed in with.'],
      ctaLabel: 'Continue to your account',
      ctaIcon: 'arrow-right',
    },
    // Softened for an authenticated visitor — never send them to "ask your admin"
    // when the link is simply spent or missing; name the live session instead.
    // Switch stays available: a dead token cannot be accepted, but the visitor
    // may still be on the wrong session and need a different account.
    invalid: {
      icon: 'alert-circle',
      headline: 'Invitation not valid',
      subline: [
        'This invitation link is no longer valid or expired.',
        "You're signed in as {currentEmail}.",
        'Continue with this email, or switch if you need a different one.',
      ],
      ctaLabel: 'Continue to your account',
      ctaIcon: 'arrow-right',
      secondaryCtaLabel: 'Sign in with a different email',
    },
    // Deliberately says nothing about the link: we never got a verdict on it.
    unavailable: {
      icon: 'wifi-off',
      headline: "We couldn't check this invitation",
      subline: ['Something went wrong on our side, not with your link.', 'Try again in a moment.'],
      ctaLabel: 'Try again',
      ctaIcon: 'refresh-cw',
      secondaryCtaLabel: 'Continue to your account',
    },
  },
  // Keyed by EMAIL_VERIFICATION_STATES. Auth0's `message` param is never
  // rendered — it only classifies the result inside the helper.
  emailVerifiedSession: {
    verified: {
      icon: 'mail-check',
      headline: 'Email verification recorded',
      subline: [
        "You're signed in as {currentEmail}.",
        'If that is not the address you verified, switch accounts.',
      ],
      ctaLabel: 'Continue to your account',
      ctaIcon: 'arrow-right',
      secondaryCtaLabel: 'Switch account',
    },
    already_verified: {
      icon: 'mail-check',
      headline: 'This link was already used',
      subline: [
        "You're signed in as {currentEmail}.",
        'If that is not the address you meant, switch accounts.',
      ],
      ctaLabel: 'Continue to your account',
      ctaIcon: 'arrow-right',
      secondaryCtaLabel: 'Switch account',
    },
    invalid: {
      icon: 'alert-circle',
      headline: 'This link is no longer valid',
      subline: [
        "You're signed in as {currentEmail}.",
        'Continue with this email, or switch if you need a different one.',
      ],
      ctaLabel: 'Continue to your account',
      ctaIcon: 'arrow-right',
      secondaryCtaLabel: 'Sign in with a different email',
    },
    no_result: {
      icon: 'user-check',
      headline: "You're already signed in",
      subline: ['Signed in as {currentEmail}.'],
      ctaLabel: 'Continue to your account',
      ctaIcon: 'arrow-right',
    },
  },
}

export const FOOTER_LINKS = [
  {
    label: 'Privacy Policy',
    href: 'https://www.armadillex.com/privacy-policy',
  },
  {
    label: 'Terms and Conditions',
    href: 'https://www.armadillex.com/terms-conditions',
  },
  {
    label: 'Terms of Usage',
    to: { name: ROUTES.TERMS },
  },
  // {
  //   label: 'Contact',
  //   href: 'https://www.armadillex.com/contact',
  // },
]

export const DRAWER_BREAKPOINT_WIDTH = 1300

/** Keep in sync with `$small-screen` in `src/css/setup/_variables.scss`. */
export const SMALL_SCREEN_WIDTH = 900

/** Keep in sync with `$semi-small-screen` in `src/css/setup/_variables.scss`. */
export const SEMI_SMALL_SCREEN_WIDTH = 1100

// Google Cloud Storage bucket for heavy/brand assets (logos, illustrations, etc.)
const BUCKET_ASSETS_BASE = 'https://storage.googleapis.com/cookiearmor-dev-assets/cookie-armor'

export const LOGO = {
  FULL: `${BUCKET_ASSETS_BASE}/ca-logo.svg`,
  FULL_WHITE: `${BUCKET_ASSETS_BASE}/ca-white-logo.svg`,
  MINI: `${BUCKET_ASSETS_BASE}/minimized-logo.svg`,
  MINI_WHITE: `${BUCKET_ASSETS_BASE}/minimized-logo-white.svg`,
}

// Cache update action types
export const CACHE_ACTIONS = {
  ADD: 'add',
  UPDATE: 'update',
  REMOVE: 'remove',
}

// BroadcastChannel: single channel name for all cross-tab events
export const BROADCAST_CHANNEL_NAME = 'cookiearmor-broadcast'

/**
 * BroadcastChannel top-level topics.
 *
 * Two buckets:
 *  - LOGOUT: session teardown — its own topic so it stays simple and obvious.
 *  - DATA_SYNC: generic cache/data sync bucket. All non-logout flows post
 *    here with a `updateType` from `BROADCAST_UPDATE_TYPES` so the receiver
 *    in `useBroadcastChannel.js` can fan them out via a single switch.
 *
 * Simple cache sync posts `BROADCAST_UPDATE_TYPES.QUERY_DATA` via
 * `broadcastQueryData` or a named `broadcast*` wrapper — no new update type
 * needed. Extend `BROADCAST_UPDATE_TYPES` only for non-trivial sync flows.
 */
export const BROADCAST_TOPICS = {
  LOGOUT: 'logout',
  DATA_SYNC: 'data-sync',
}

/**
 * Discriminators carried inside `BROADCAST_TOPICS.DATA_SYNC` payloads.
 *
 * - QUERY_DATA: generic cross-tab `setQueryData` — payload carries `queryKey`
 *   + `data`. Most flows use named `broadcast*` helpers that post this type.
 * - SCAN_STATUS: dual-cache mirror — updates `[SCAN_STATUS, siteId]` and
 *   syncs scan history from the status payload in one receiver branch.
 * - SCAN_CATEGORY_OVERRIDE: classification result sync — updates scan status,
 *   history, and banner denormalized counts from the PATCH response.
 * - BANNER: semantic entity sync — drive cache-handler
 *   updates (detail + list for banners) in the receiver.
 * - BANNER_REMOVE: list replace + per-site cache eviction + scan dismiss
 *   (removeQueries / multi-cache orchestration).
 * - Add a new entry only when sync needs more than a single `setQueryData`
 *   (multi-cache orchestration, removeQueries, etc.).
 */
export const BROADCAST_UPDATE_TYPES = {
  QUERY_DATA: 'query-data',
  SCAN_STATUS: 'scan-status-sync',
  SCAN_CATEGORY_OVERRIDE: 'scan-category-override',
  SCAN_REGISTRY: 'scan-registry',
  BANNER: 'banner',
  BANNER_REMOVE: 'banner-remove',
}

// List-mode q-table scrolls on tbody (thead stays fixed). Pass to useScrollToTop from q-table ref.
export const TABLE_SCROLL_SELECTOR = 'tbody'

/** Allowed client-side table page sizes (shared rows-per-page selector). */
export const TABLE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

/** Default rows per page when URL omits the param. */
export const DEFAULT_TABLE_ROWS_PER_PAGE = 10

/** Preset date-range filter options shared across feature tables. */
export const DATE_RANGE_FILTER_OPTIONS = [
  { value: null, label: 'All time', icon: 'calendar' },
  { value: '1w', label: 'Last 7 days', icon: 'calendar' },
  { value: '1m', label: 'Last month', icon: 'calendar-days' },
  { value: '3m', label: 'Last 3 months', icon: 'calendar-range' },
]

/** All valid date range values for URL `?dateRange` validation. */
export const VALID_DATE_RANGE_KEYS = new Set(
  DATE_RANGE_FILTER_OPTIONS.filter((opt) => opt.value !== null).map((opt) => opt.value)
)
